import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function HotelManagement() {
    const [hotels, setHotels] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        address: "",
        image: null,
    });
    const [loading, setLoading] = useState(false);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [hotelToDelete, setHotelToDelete] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    const navigate = useNavigate();

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const fetchHotels = async () => {
        try {
            const res = await axios.get(
                "https://api.pdf.tajgateways.com/api-v1/hotels/get-hotels"
            );
            setHotels(res.data);
        } catch (err) {
            console.error("Error fetching hotels:", err);
            toast.error("❌ Failed to fetch hotels.");
        }
    };

    useEffect(() => {
        fetchHotels();
    }, []);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === "image") {
            setFormData({ ...formData, image: files[0] });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const payload = new FormData();
        payload.append("name", formData.name);
        payload.append("address", formData.address);
        if (formData.image) payload.append("image", formData.image);

        try {
            const res = await axios.post(
                "https://api.pdf.tajgateways.com/api-v1/hotels/create",
                payload
            );

            if (res.data) {
                toast.success("✅ Hotel added successfully!", {
                    position: "top-right",
                    autoClose: 3000,
                });
                navigate("/");
            }

            setShowForm(false);
            setFormData({ name: "", address: "", image: null });
            fetchHotels();
        } catch (err) {
            console.error("Error submitting form:", err);
            toast.error("❌ Failed to submit. Please try again.", {
                position: "top-right",
                autoClose: 3000,
            });
        } finally {
            setLoading(false);
        }
    };

    const confirmDelete = (hotel) => {
        setHotelToDelete(hotel);
        setShowDeletePopup(true);
    };

    const handleDelete = async () => {
        if (!hotelToDelete) return;
        try {
            await axios.delete(
                `https://api.pdf.tajgateways.com/api-v1/hotels/delete/${hotelToDelete._id}`
            );
            toast.success("✅ Hotel deleted successfully!", {
                position: "top-right",
                autoClose: 3000,
            });
            setShowDeletePopup(false);
            setHotelToDelete(null);
            fetchHotels();
        } catch (err) {
            console.error("Error deleting hotel:", err);
            toast.error("❌ Failed to delete hotel.", {
                position: "top-right",
                autoClose: 3000,
            });
        }
    };

    const responsiveStyles = {
        formWidth: isMobile ? "90%" : "400px",
        tableFont: isMobile ? "12px" : "14px",
        thPadding: isMobile ? "8px" : "12px",
        tdPadding: isMobile ? "8px" : "12px",
        titleFont: isMobile ? "18px" : "24px",
        headerFlex: isMobile ? "column" : "row",
        tableOverflow: isMobile ? "scroll" : "visible",
    };

    return (
        <div style={styles.container}>
            <ToastContainer />
            <div
                style={{
                    ...styles.header,
                    flexDirection: responsiveStyles.headerFlex,
                    gap: isMobile ? "10px" : "0",
                }}
            >
                <h2 style={{ ...styles.title, fontSize: responsiveStyles.titleFont }}>
                    🏨 Hotel Management
                </h2>

                <div style={styles.actions}>
                    <button
                        style={styles.addBtn}
                        onClick={() => {
                            setShowForm(true);
                            setFormData({ name: "", address: "", image: null });
                        }}
                    >
                        ➕ Add Hotel
                    </button>

                    <button
                        type="button"
                        style={styles.dashboardBtn}
                        onClick={() => navigate("/")}
                    >
                        📊 Dashboard
                    </button>
                </div>
            </div>

            {/* Form */}
            {showForm && (
                <div style={styles.formContainer}>
                    <form
                        style={{ ...styles.form, width: responsiveStyles.formWidth }}
                        onSubmit={handleSubmit}
                    >
                        <h3 style={styles.formTitle}>Add Hotel</h3>

                        <input
                            style={styles.input}
                            type="text"
                            name="name"
                            placeholder="Hotel Name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                        />

                        <input
                            style={styles.input}
                            type="text"
                            name="address"
                            placeholder="Hotel Address"
                            value={formData.address}
                            onChange={handleChange}
                            required
                        />

                        <input style={styles.input} type="file" name="image" onChange={handleChange} />

                        <button style={styles.button} type="submit" disabled={loading}>
                            {loading ? (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "8px",
                                    }}
                                >
                                    <div style={styles.loader}></div> Submitting...
                                </div>
                            ) : (
                                "Submit"
                            )}
                        </button>
                    </form>
                </div>
            )}

            {/* Hotel List */}
            <div style={{ overflowX: responsiveStyles.tableOverflow }}>
                <table
                    style={{
                        ...styles.table,
                        fontSize: responsiveStyles.tableFont,
                        minWidth: isMobile ? "600px" : "100%",
                    }}
                >
                    <thead>
                        <tr>
                            <th style={{ ...styles.th, padding: responsiveStyles.thPadding }}>Name</th>
                            <th style={{ ...styles.th, padding: responsiveStyles.thPadding }}>Address</th>
                            <th style={{ ...styles.th, padding: responsiveStyles.thPadding }}>Image</th>
                            <th style={{ ...styles.th, padding: responsiveStyles.thPadding }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {hotels.length > 0 ? (
                            hotels.map((hotel) => (
                                <tr key={hotel._id}>
                                    <td style={{ ...styles.td, padding: responsiveStyles.tdPadding }}>
                                        {hotel.name}
                                    </td>
                                    <td style={{ ...styles.td, padding: responsiveStyles.tdPadding }}>
                                        {hotel.address}
                                    </td>
                                    <td style={{ ...styles.td, padding: responsiveStyles.tdPadding }}>
                                        {hotel.image ? (
                                            <img
                                                src={`https://api.pdf.tajgateways.com${hotel.image}`}
                                                alt={hotel.name}
                                                style={{
                                                    width: isMobile ? "50px" : "80px",
                                                    borderRadius: "4px",
                                                }}
                                            />
                                        ) : (
                                            "-"
                                        )}
                                    </td>
                                    <td style={{ ...styles.td, padding: responsiveStyles.tdPadding }}>
                                        <button
                                            style={styles.deleteBtn}
                                            onClick={() => confirmDelete(hotel)}
                                        >
                                            🗑️
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" style={styles.noData}>
                                    No hotels available.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Delete Confirmation Popup */}
            {showDeletePopup && (
                <div style={styles.popupOverlay}>
                    <div style={{ ...styles.popup, width: isMobile ? "90%" : "300px" }}>
                        <h3>⚠️ Confirm Delete</h3>
                        <p>
                            Are you sure you want to delete{" "}
                            <strong>{hotelToDelete?.name}</strong>?
                        </p>
                        <div
                            style={{
                                ...styles.popupActions,
                                flexDirection: isMobile ? "column" : "row",
                                gap: "10px",
                            }}
                        >
                            <button
                                style={styles.cancelBtn}
                                onClick={() => setShowDeletePopup(false)}
                            >
                                ❌ Cancel
                            </button>
                            <button style={styles.confirmBtn} onClick={handleDelete}>
                                ✅ Yes, Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = {
    container: { padding: "20px" },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
    },
    title: { fontSize: "24px", fontWeight: "600" },
    actions: { display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" },
    dashboardBtn: {
        background: "#3498db",
        color: "#fff",
        padding: "8px 14px",
        border: "none",
        borderRadius: "8px",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "500",
    },
    addBtn: {
        background: "#3498db",
        color: "#fff",
        padding: "10px 15px",
        border: "none",
        borderRadius: "5px",
        cursor: "pointer",
    },
    formContainer: {
        margin: "20px 0",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    form: {
        background: "#ffffff",
        padding: "20px",
        borderRadius: "10px",
        boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
        display: "flex",
        flexDirection: "column",
        gap: "15px",
    },
    formTitle: { textAlign: "center", color: "#2c3e50" },
    input: {
        padding: "10px",
        borderRadius: "5px",
        border: "1px solid #ccc",
        fontSize: "14px",
    },
    button: {
        background: "#3498db",
        color: "#fff",
        padding: "10px",
        border: "none",
        borderRadius: "5px",
        cursor: "pointer",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        marginTop: "20px",
        textAlign: "left",
    },
    th: {
        background: "#3498db",
        color: "#fff",
        textAlign: "left",
    },
    td: { borderBottom: "1px solid #ddd", verticalAlign: "middle" },
    deleteBtn: {
        background: "#e74c3c",
        color: "#fff",
        padding: "5px 20px",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
    },
    noData: { textAlign: "center", padding: "20px" },
    popupOverlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    popup: {
        background: "#fff",
        padding: "20px",
        borderRadius: "10px",
        textAlign: "center",
        boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
    },
    popupActions: {
        marginTop: "20px",
        display: "flex",
        justifyContent: "space-around",
    },
    cancelBtn: {
        background: "#7f8c8d",
        color: "#fff",
        padding: "8px 12px",
        border: "none",
        borderRadius: "5px",
        cursor: "pointer",
    },
    confirmBtn: {
        background: "#e74c3c",
        color: "#fff",
        padding: "8px 12px",
        border: "none",
        borderRadius: "5px",
        cursor: "pointer",
    },
    loader: {
        border: "2px solid #f3f3f3",
        borderTop: "2px solid #fff",
        borderRadius: "50%",
        width: "14px",
        height: "14px",
        animation: "spin 1s linear infinite",
    },
};

// Inject keyframes for spin animation
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;
document.head.appendChild(styleSheet);


